import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { ICustomTranslation } from "@ryde/shared";

const HIGHLIGHT_DURATION_MS = 3500;

interface IUseCustomTranslations {
  rows: ICustomTranslation[];
  filteredRows: { row: ICustomTranslation; index: number }[];
  existingPaths: Set<string>;
  highlightedPath: string | null;
  search: string;
  setSearch: (value: string) => void;
  addRow: (row: ICustomTranslation) => boolean;
  updateRow: (index: number, patch: Partial<ICustomTranslation>) => void;
  removeRow: (index: number) => void;
}

const matchesSearch = (row: ICustomTranslation, query: string): boolean => {
  const normalized = query.trim().toLowerCase();

  if (!normalized) {
    return true;
  }

  return (
    row.path.toLowerCase().includes(normalized) ||
    row.en.toLowerCase().includes(normalized) ||
    row.he.toLowerCase().includes(normalized)
  );
};

/**
 * Owns the custom-translations array within the parent dynamic form context.
 * Components stay presentation-only — all reads/writes go through here.
 */
export const useCustomTranslations = (name: string): IUseCustomTranslations => {
  const { control, setValue, clearErrors } = useFormContext();
  const [search, setSearch] = useState("");
  const [highlightedPath, setHighlightedPath] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    [],
  );

  const value = useWatch({ control, name });
  const rows: ICustomTranslation[] = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  const commit = useEvent((next: ICustomTranslation[]): void => {
    setValue(name, next, { shouldDirty: true, shouldValidate: true });
    clearErrors(name);
  });

  const existingPaths = useMemo(
    () => new Set(rows.map(row => row.path)),
    [rows],
  );

  const filteredRows = useMemo(
    () =>
      rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => matchesSearch(row, search)),
    [rows, search],
  );

  const addRow = useEvent((row: ICustomTranslation): boolean => {
    if (existingPaths.has(row.path)) {
      return false;
    }

    commit([row, ...rows]);

    // Flash the newly added row so the user can spot the latest entry.
    setHighlightedPath(row.path);

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedPath(null);
    }, HIGHLIGHT_DURATION_MS);

    return true;
  });

  const updateRow = useEvent(
    (index: number, patch: Partial<ICustomTranslation>): void => {
      commit(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    },
  );

  const removeRow = useEvent((index: number): void => {
    commit(rows.filter((_, i) => i !== index));
  });

  return {
    rows,
    filteredRows,
    existingPaths,
    highlightedPath,
    search,
    setSearch,
    addRow,
    updateRow,
    removeRow,
  };
};
